import React, {useMemo} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {FirebaseMessageRoomItemPayload, parseShape} from 'types'
import {getFontFamily, getFontSize, parseDate} from 'utils'
import {Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'

const StyledParagraph = styled(Paragraph)`
  white-space: pre-wrap;
`
const StyledContentInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  margin-left: ${convertUnit(10)};
`
const StyledSeparator = styled.div`
  ${({theme}) => ({backgroundColor: theme.gray_1})}
  height: ${convertUnit(1)};
  display: flex;
  width: 100%;
  margin: ${convertUnit(5)} 0;
`
const StyledItemContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin: ${convertUnit(5)} 0;
`
const StyledUploadDate = styled(Paragraph)`
  margin-bottom: ${convertUnit(16)};
`

interface GiftShopMessageRoomContentItemNotificationDeleteRequestProps {
  payload: FirebaseMessageRoomItemPayload['notification_report_delete_content']
}

export default function GiftShopMessageRoomContentItemNotificationDeleteRequest({
  payload,
}: GiftShopMessageRoomContentItemNotificationDeleteRequestProps) {
  const {
    content_original_at,
    content_title,
    content_fototree_name,
    content_created_at,
    username,
  } = payload
  const {translate} = useTranslation()

  const handleRenderContent = useMemo(
    () => (
      <StyledItemContainer>
        <StyledContentInfo>
          {content_created_at && (
            <StyledUploadDate fontSize="s" color="gray_5">
              {`${translate('global:uploadDate', {
                date: parseDate(content_created_at, 'DD MMMM YYYY'),
              })}\n`}
            </StyledUploadDate>
          )}
          <Paragraph fontSize="m" fontWeight="bold">
            {content_title}
          </Paragraph>
          <StyledSeparator />
          <Paragraph fontSize="m">
            {parseDate(content_original_at, 'DD MMMM YYYY')}
          </Paragraph>
          {content_fototree_name && (
            <Paragraph fontSize="m">{content_fototree_name}</Paragraph>
          )}
        </StyledContentInfo>
      </StyledItemContainer>
    ),
    [
      content_created_at,
      content_fototree_name,
      content_original_at,
      content_title,
      translate,
    ],
  )

  const parseText = useMemo<parseShape[]>(
    () => [
      {
        pattern: /<b>.*?<\/b>/,
        style: {
          fontFamily: getFontFamily('bold'),
          fontSize: getFontSize('m'),
        },
        renderText: (text) => text.replace(/<b>|<\/b>/g, ''),
      },
    ],
    [],
  )

  return (
    <>
      <StyledParagraph fontSize="m">
        <ParsedText parse={parseText}>
          {translate('messages:notificationDeleteRequest', {username})}
        </ParsedText>
      </StyledParagraph>
      {handleRenderContent}
      <StyledParagraph fontSize="m">
        <ParsedText parse={parseText}>
          {translate('messages:notificationDeleteRequestFooter')}
        </ParsedText>
      </StyledParagraph>
    </>
  )
}
